import React, {useCallback, useEffect, useState} from 'react';
import {Box, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const TextBox = ({
                     index,
                     componentId,
                     value,
                     onChange,
                     descriptionHtml,
                     onDescriptionHtmlChange,
                     commitDescriptionHtml,
                     enableValidation,
                     isEditMode,
                     isFormArchived,
                     readonly,
                     placeholderValues,
                     accessLevel,
                     submitMode,
                     error,
                     audience,
                     recipientType,
                     linkedToStudents,
                     linkedToEmployees,
                     simsUserDefinedField,
                 }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml ?? '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml ?? '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    return (
        <Box key={`TextBox-${componentId}`} sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            index={index}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                        <RequiredCheckbox
                            enableValidation={enableValidation}
                            index={index}
                            onChange={onChange}
                            isFormArchived={isFormArchived}
                        />
                        {/* AD-2642: the answer is written to this user defined field on the child's SIMS record */}
                        <TextField
                            size="small"
                            sx={{mt: 1, width: 360, maxWidth: '100%'}}
                            label="SIMS user defined field"
                            value={simsUserDefinedField ?? ''}
                            disabled={isFormArchived}
                            inputProps={{maxLength: 100, 'data-testid': 'textbox-sims-udf'}}
                            onChange={e => onChange({index, simsUserDefinedField: e.target.value})}
                        />
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readonly={readonly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />
                        {!readonly
                            ? (
                                <TextField
                            fullWidth
                            required={enableValidation}
                            multiline
                            value={value}
                            disabled={!submitMode}
                            error={!!error}
                            helperText={error}
                            onChange={e => onChange({
                                id: componentId,
                                value: e.target.value
                            })}
                                />
                            )
                            : (
                                <TextField
                            fullWidth
                            multiline
                            defaultValue={value}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                            )}
                    </>
                )}
        </Box>
    );
};

TextBox.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.string,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    simsUserDefinedField: PropTypes.string,
};

export default TextBox;
